import React, { useContext, useState } from 'react'
import { useForm } from "react-hook-form";
import * as yup from "yup"
import { yupResolver } from "@hookform/resolvers/yup";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useFirebase } from "react-redux-firebase"

import Button from '@/components/Button'
import InputReveal from '@/components/InputReveal'
import { AuthContext } from '../../../contexts/AuthContext';
import resolveErrors from '@/utils/resolveErrors'
import { toast } from 'react-toastify';
import Toast from '@/components/Toast'
import { CogIcon } from '@heroicons/react/solid';

export default function SettingsSecurity() {
  const { auth } = useContext(AuthContext)
  const isGoogleAuthUser = auth?.providerData[0]?.providerId == "google.com"
  return (
    <div className="grid gap-6">
      {isGoogleAuthUser ? <SocialRegisterUser /> : <PasswordForm />}
      {/* <TwoFactorForm /> */}
    </div>
  )
}

const SocialRegisterUser = () => {
  return (
    <div className="py-8 text-center text-gray-300">Changing account passwords is not supported for user accounts created via Social Registration.</div>
  )
}

const PasswordForm = () => {
  const { auth } = useContext(AuthContext)
  const firebase = useFirebase()
  const functions = getFunctions();
  const updateUserPassword = httpsCallable(functions, 'updateUserPassword');

  // const { updateNewPassword } = useContext(AuthContext)
  const [serverErrors, setServerErrors] = useState(null)
  const schema = yup.object().shape({
    oldPassword: yup.string().required(),
    newPassword: yup.string().required(),
    confirmNewPassword: yup.string().oneOf([yup.ref('newPassword'), null], 'Passwords must match').required(),
  });

  const { register, setValue, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    }
  });

  const onSubmitHandler = async (data) => {
    // make some firebase call to resend verification email
    try {
      if (auth?.email) {
        await updateUserPassword({ email: auth.email, password: data.oldPassword, newPassword: data.newPassword, confirmNewPassword: data.confirmNewPassword })
          .then(async () => {
            setValue('oldPassword', '')
            setValue('newPassword', '')
            setValue('confirmNewPassword', '')
            toast(<Toast icon={<CogIcon className="h-6 w-6 text-gray-300" />} text={`Password updated successfully. Please log back in.`} />)
            await firebase.logout()
          })
      }
    } catch (err) {
      setServerErrors(resolveErrors(err))
    }
    setValue('oldPassword', '')
    setValue('newPassword', '')
    setValue('confirmNewPassword', '')
  }

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)} className="border border-gray-800 rounded">
      <div className="px-4">
        <h2 className="pt-4 pb-2 font-medium text-xl">Password</h2>
        <div className="py-3 grid gap-4">
          <div className="lg:max-w-[50%]">
            <label className="mb-1 block font-medium text-gray-300">Old Password</label>
            <InputReveal error={errors?.oldPassword} {...register('oldPassword')} onChange={e => setValue('oldPassword', e.target.value)} />
          </div>
          <div className="lg:max-w-[50%]">
            <label className="mb-1 block font-medium text-gray-300">New Password</label>
            <InputReveal error={errors?.newPassword} {...register('newPassword')} onChange={e => setValue('newPassword', e.target.value)} />
          </div>
          <div className="lg:max-w-[50%]">
            <label className="mb-1 block font-medium text-gray-300">Confirm New Password</label>
            <InputReveal error={errors?.confirmNewPassword} {...register('confirmNewPassword')} onChange={e => setValue('confirmNewPassword', e.target.value)} />
          </div>
        </div>
      </div>
      <div className="bg-gray-800 bg-opacity-50 border-t border-gray-800 px-4 py-3 flex justify-between">
        <p>{serverErrors}</p>
        <Button modifier="secondary" rounded="rounded-full" type="submit" className="py-2.5 px-4">
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              Processing
              <svg className="animate-spin ml-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </span>
          ) : (
            <span>Save</span>
          )}
        </Button>
      </div>
    </form>
  )
}

const TwoFactorForm = () => {
  const schema = yup.object().shape({
    password: yup.string().required(),
    twoFactorCode: yup.string().required(),
  });

  const { register, setValue, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      password: '',
      twoFactorCode: '',
    }
  });

  const onSubmitHandler = data => {
    console.log(data)
  }

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)} className="border border-gray-800 rounded">
      <div className="px-4">
        <div className="py-4 border-b border-gray-800">
          <h2 className="font-medium text-xl mb-2">Two Factor</h2>
          <p className="text-gray-300">To keep your account extra secure leave a two factor authentication enabled.</p>
        </div>
        <div className="py-3 grid gap-4">
          <div className="lg:max-w-[50%]">
            <label className="mb-1 block font-medium text-gray-300">Copy this code to your authenticator app</label>
            <input value="DSFKJDHSFJKHASDFLJHASDF" type="text" className="bg-gray-700 border-transparent shadow-md" readOnly />
          </div>
          <div className="lg:max-w-[50%]">
            <label className="mb-3 block font-medium text-gray-300">Don't let anyone see this!</label>
            <div className="h-36 w-36 bg-white"></div>
          </div>
          <div className="lg:max-w-[50%]">
            <label className="mb-1 block font-medium text-gray-300">Password</label>
            <InputReveal error={errors?.confirmNewPassword} {...register('password')} onChange={e => setValue('password', e.target.value)} />
          </div>
          <div className="lg:max-w-[50%]">
            <label className="mb-3 block font-medium text-gray-300">Two Factor Code</label>
            <input {...register('twoFactorCode')} type="text" />
          </div>
        </div>
      </div>
      <div className="bg-gray-800 bg-opacity-50 border-t border-gray-800 px-4 py-3 flex justify-end">
        <Button disabled className="py-2.5 px-4">Enable</Button>
      </div>
    </form>
  )
}